import { DialogTrigger } from '@radix-ui/react-dialog'
import React, { useContext, useEffect, useState } from 'react'

import Dialog from '@/components/shared/ui/Dialog'
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/shared/ui/Dialog/Dialog'
import Tabs from '@/components/shared/ui/Tabs'
import {
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/shared/ui/Tabs/Tabs'
import Typography from '@/components/shared/ui/Typography'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import { EventEmitter } from '@/lib/eventEmitter'
import type { IPost } from '@/types/post'
import type { IPostAddon } from '@/types/post/postAddon'

import PushToButton from '../PushToButton'
import CreateGithubIssue from './CreateGithubIssue'
import LinkGithubIssue from './LinkGithubIssue'

interface IPropTypes {
  post: IPost
}

export default function PushToGithub({ post }: IPropTypes) {
  const t = useTranslations(`post.integrations.github`)
  const { integrations } = useContext(HNContext)
  const [show, setShow] = useState(false)
  const [canCreate, setCanCreate] = useState(Boolean(post.can_push_to_github))

  const handleCreate = (issue: IPostAddon) => {
    if (issue.action === 'push') setCanCreate(false)
    EventEmitter.dispatch('NEW_POST_ADDON', {
      provider: issue.provider.toLowerCase(),
      addon: issue,
      action: issue.action,
    })
    setShow(false)
  }

  useEffect(() => setCanCreate(Boolean(post?.can_push_to_github)), [post])

  if (!integrations?.can_manage_github) return null

  return (
    <Dialog open={show} onOpenChange={setShow}>
      <DialogTrigger asChild>
        <PushToButton
          onClick={() => setShow(true)}
          provider={`github`}
          pushText={t(`pushToGithub`)}
          linkText={t(`linkToGithub`)}
          canCreate={Boolean(canCreate)}
          onChangeCanCreate={(value: boolean) => setCanCreate(value)}
        />
      </DialogTrigger>
      <DialogContent size='lg' backdrop>
        <DialogTitle>{t(`title`)}</DialogTitle>
        <DialogDescription>
          <span className='flex flex-col space-y-1'>
            <Typography.Text>{t(`description`)}</Typography.Text>
            <Typography.Text className='text-xs'>{t(`note`)}</Typography.Text>
          </span>
        </DialogDescription>
        <Tabs defaultValue={canCreate ? 'create' : 'link'}>
          <TabsList>
            {canCreate && (
              <TabsTrigger value='create'>
                <span>{t('create.title')}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value='link'>
              <span>{t('link.title')}</span>
            </TabsTrigger>
          </TabsList>
          {canCreate && (
            <TabsContent value='create'>
              <CreateGithubIssue post={post} onLink={handleCreate} />
            </TabsContent>
          )}
          <TabsContent value='link'>
            <LinkGithubIssue post={post} onLink={handleCreate} />
          </TabsContent>
        </Tabs>
        <DialogClose />
      </DialogContent>
    </Dialog>
  )
}
